import React, { useContext, useEffect, useRef } from "react";
import "./EditModeTimeline.scss";
import { TimeEntryRepeater } from "../../Repeaters/TimeEntryRepeater/TimeEntryRepeater";
import { Timescale } from "../../Components/Library/Timescale/Timescale";
import { EditModeContext } from "../../Context/EditModeContext/EditModeContext";
import {
	TimelineContext,
	defaultScale,
	IncreaseScaleByStep,
	DecreaseScaleByStep,
	END_OF_DAY_BUFFER_HOURS,
} from "../../Context/TimelineContext/TimelineContext";
import { TimelineContextDispatchActionType } from "../../Context/TimelineContext/TimelineContextDispatchActionType";
import { DateTime } from "luxon";
import nameof from "ts-nameof.macro";
import { HistoryModel } from "../../Hooks/useHistory/HistoryModel";
import { useHistory } from "../../Hooks/useHistory/useHistory";
import { TheHook } from "../../Hooks/TheHook/TheHook";
import { ITimeEntry } from "../../Data/Models/ITimeEntry";
import { ViewContext } from "../../Context/ViewContext/ViewContext";
import { ViewContextDispatchActionType } from "../../Context/ViewContext/ViewContextDispatchActionType";
import { ModalContext } from "../../Context/ModalContext/ModalContext";
import { ModalContextDispatchActionType } from "../../Context/ModalContext/ModalContextDispatchActionType";
import { AlertContext } from "../../Context/AlertContext/AlertContext";
import { AlertContextDispatchActionType } from "../../Context/AlertContext/AlertContextDispatchActionType";
import { ContextMenu, useContextMenuParams } from "../../Components/Library/ContextMenu/ContextMenu";
import { GetRelativeClickCoordinates } from "../../GlobalUtils/PositionUtils";
import { CalcTimePosition, CalcPositionTime } from "../../GlobalUtils/TimeUtils";
import { ImContext } from "../../Context/DbContext/DbContext";
import { useTime } from "../../Hooks/useTime";
import { UnsavedAlert } from "./UnsavedAlert/UnsavedAlert"

export function EditModeTimeline() {
	const im = useContext(ImContext);
	const editModeContext = useContext(EditModeContext);
	const timelineContext = useContext(TimelineContext);
	const viewContext = useContext(ViewContext);
	const modalContext = useContext(ModalContext);
	const alertContext = useContext(AlertContext);
	const history: HistoryModel = useHistory(nameof(EditModeTimeline));
	const theHook = TheHook(nameof(EditModeTimeline), true);
	const currentTime = useTime();

	const scrollRef = useRef<HTMLElement>(null);
	const scale = timelineContext.state.currentScale ? timelineContext.state.currentScale : defaultScale;

	useEffect(() => {
		theHook.SetupEditMode();

		// If this is the modal version of the edit timeline, set a custom close function
		if (modalContext.state.showModal) {
			modalContext.dispatch({
				type: ModalContextDispatchActionType.SetCustomCloseCallback,
				payload: UnsavedChangesAlert,
			});
		}
	}, []);

	useEffect(() => {
		// Update the values used in UsavedChangesAlert
		modalContext.dispatch({
			type: ModalContextDispatchActionType.SetCustomCloseCallback,
			payload: UnsavedChangesAlert,
		});
	}, [history.canUndo, history.canRedo]);

	useEffect(() => {
		if (timelineContext.state.ScrollToDate) {
			ScrollToDate(timelineContext.state.ScrollToDate);
		} else if (timelineContext.state.ScrollToTimeEntry) {
			ScrollToTimeEntry(timelineContext.state.ScrollToTimeEntry);
		} else {
			ScrollToBottom();
		}
	}, [timelineContext.state.ScrollToDate, timelineContext.state.ScrollToTimeEntry]);

	function ScrollToBottom() {
		if (scrollRef && scrollRef.current) {
			scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
		}
	}

	function ScrollToSetHeight(height: number) {
		if (scrollRef && scrollRef.current) {
			let offset = 0;
			if (scrollRef.current.getBoundingClientRect) {
				offset = scrollRef.current.getBoundingClientRect().height / 2;
			}

			scrollRef.current.scrollTop = height - offset;
		}
	}

	function ScrollToDate(date: DateTime) {
		const height = CalcTimePosition(
			date,
			timelineContext.state.timelineStartTimeOffsetHours,
			timelineContext.state.currentScale,
			timelineContext.state.currentDayOffset
		);
		ScrollToSetHeight(height);
	}

	function ScrollToTimeEntry(timeEntry: ITimeEntry) {
		console.log("scroll to time entry ");

		const endTime = timeEntry.endedWhen ? timeEntry.endedWhen : im.timeSource.GetLocalTime();
		const midTime = endTime.minus({
			seconds: endTime.diff(timeEntry.startedWhen, "seconds").seconds / 2,
		});

		const height = CalcTimePosition(
			midTime,
			timelineContext.state.timelineStartTimeOffsetHours,
			timelineContext.state.currentScale,
			timelineContext.state.currentDayOffset
		);

		ScrollToSetHeight(height);
	}

	function IncreaseScale() {
		const newScale = IncreaseScaleByStep(timelineContext.state.currentScale);
		ChangeScale(newScale);
	}

	// `tempScale` purely serves as a way of modifying the scale inside the current
	// render. Otherwise we have to wait for the context update, which is asynchronous
	// and there's no way of knowing when that'll complete
	let tempScale = timelineContext.state.currentScale;

	function ChangeScale(newScale: number) {
		if (!scrollRef.current) throw new Error("scrollRef not available");
		const middleHeight = scrollRef.current.offsetHeight / 2;
		const middlePos = scrollRef.current.scrollTop + middleHeight;
		const scrolledToTime = CalcPositionTime(
			middlePos,
			timelineContext.state.timelineStartTimeOffsetHours,
			timelineContext.state.currentScale,
			timelineContext.state.currentDayOffset
		);

		const newScrollTop =
			CalcTimePosition(
				scrolledToTime,
				timelineContext.state.timelineStartTimeOffsetHours,
				newScale,
				timelineContext.state.currentDayOffset
			) - middleHeight;

		// If we can't scroll to the position we want to because the scroll area isn't tall enough yet,
		// we'll have to wait for the render to complete before setting the new scrollTop
		if (newScrollTop + scrollRef.current.offsetHeight < scrollRef.current.scrollHeight)
			scrollRef.current.scrollTop = newScrollTop;
		else
			setImmediate(() => {
				if (scrollRef.current) scrollRef.current.scrollTop = newScrollTop;
			});

		tempScale = newScale;
		timelineContext.dispatch({
			type: TimelineContextDispatchActionType.SetCurrentScale,
			payload: newScale,
		});
	}

	function DecreaseScale() {
		const newScale = DecreaseScaleByStep(timelineContext.state.currentScale);
		ChangeScale(newScale);
	}

	const { contextMenuParams, setMenuCoords, setMenuOptions, setShowContextMenu } = useContextMenuParams();

	function HandleContextMenu(e: React.MouseEvent<HTMLElement, MouseEvent>) {
		e.preventDefault();
		e.stopPropagation();
		if (!scrollRef.current) return;
		const _menuCoords = GetRelativeClickCoordinates(e.nativeEvent, scrollRef.current);
		_menuCoords.y += scrollRef.current.scrollTop;

		setMenuOptions([{ display: "Insert time entry", callback: () => handleInsertTimeEntry(_menuCoords.y) }]);

		setMenuCoords(_menuCoords);
		setShowContextMenu(true);
	}

	// function EnterEditMode() {
	// 	theHook.SetupEditMode();
	// }

	async function handleInsertTimeEntry(yPosition: number) {
		const timeForPos = CalcPositionTime(
			yPosition,
			timelineContext.state.currentDayOffset || 0,
			timelineContext.state.currentScale || 1,
			timelineContext.state.currentDayOffset
		);
		console.debug("Adding new time entry", yPosition, timeForPos);
		await theHook.InsertNewTimeEntry(timeForPos, timeForPos.plus({ minutes: 10 }));
	}

	const confirmUnsavedAlert = <UnsavedAlert onSave={SaveEditMode} onDontSave={CancelEditMode} />

	function UnsavedChangesAlert() {
		if (history.canUndo || history.canRedo) {
			alertContext.dispatch({
				type: AlertContextDispatchActionType.SetAlertComponent,
				payload: confirmUnsavedAlert
			})

			alertContext.dispatch({
				type: AlertContextDispatchActionType.SetPreventClose,
				payload: false,
			})

			alertContext.dispatch({
				type: AlertContextDispatchActionType.OpenAlert,
				payload: true,
			})
		} else {
			CancelEditMode();
		}
	}


	function CancelEditMode() {
		theHook.CancelEditMode();
		Cleanup();
	}

	function SaveEditMode() {
		theHook.SaveEditMode();
		Cleanup();
	}

	function CurrentDay() {
		let currentDay = "TODAY";

		if (editModeContext.state.editModeDayOffset !== 0) {
			if (editModeContext.state.editModeDayOffset === 1) {
				currentDay = "YESTERDAY";
			} else {
				currentDay = im.timeSource
					.GetLocalTime()
					.minus({ days: editModeContext.state.editModeDayOffset })
					.toLocaleString(DateTime.DATE_MED);
			}
		}

		return currentDay;
	}

	function Cleanup() {
		timelineContext.dispatch({
			type: TimelineContextDispatchActionType.ScrollToTimeEntry,
			payload: undefined,
		});

		timelineContext.dispatch({
			type: TimelineContextDispatchActionType.ScrollToDate,
			payload: undefined,
		});

		if (modalContext.state.showModal) {
			modalContext.dispatch({
				type: ModalContextDispatchActionType.SetCustomCloseCallback,
				payload: undefined,
			});
			modalContext.dispatch({
				type: ModalContextDispatchActionType.CloseModal,
				payload: undefined,
			});
		} else {
			// If this is the edit timeline view then open to the comments view on close
			viewContext.dispatch({
				type: ViewContextDispatchActionType.CommentsView,
			});
		}

		if (alertContext.state.showAlert) {
			alertContext.dispatch({
				type: AlertContextDispatchActionType.CloseAlert,
				payload: true,
			})
		}
	}


	function CalculateUneditableAreaHeight() {
		const minutesToEndOfTimeline = (END_OF_DAY_BUFFER_HOURS * 60) - currentTime.minute
		const height = minutesToEndOfTimeline * scale;

		return height;
	}

	function CreateNoContextMenu(e: React.MouseEvent<HTMLElement, MouseEvent>) {
		// NOTE: Without this the timeline breaks as it tries to make a custom context menu below the uneditable space. This is a temporary fix.
		setShowContextMenu(false);
	}

	return (
		<section id="timeline-editor-wrapper">
			<div className="timeline-editor-container">
				<section className="timeline-header-wrapper">
					<div className="timeline-zoom-controller-wrapper">
						<button aria-label="Zoom out" onClick={DecreaseScale} className="pill sz-2 zoom-out" />
						<span className="zoom-scale">{`${(tempScale / defaultScale) * 100}%`}</span>
						<button aria-label="Zoom in" onClick={IncreaseScale} className="pill sz-2 zoom-in" />
					</div>
					<div className="timeline-date-calander">
						<h2>{CurrentDay()}</h2>
					</div>
				</section>
				<section className="timeline-content-wrapper mode-timeline-default" ref={scrollRef} onContextMenu={HandleContextMenu}>
					<div className="timeline-content-container">
						<TimeEntryRepeater
							HandleContextMenu={HandleContextMenu}
							// LinkGroupToActiveTask={props.LinkGroupToActiveTask}
							// GroupUnlink={props.GroupUnlink}
							scrollRef={scrollRef.current!}
						/>
						<span className="timeline-uneditable-space"
							style={{
								top: `${CalcTimePosition(currentTime, 0, scale, 0)}px`,
								height: `${CalculateUneditableAreaHeight()}px`
							}}
							onContextMenu={CreateNoContextMenu}
						>
							<div className="current-time-tag">
								<span className="time-stamp">{currentTime.toFormat("HH:mm")}</span>
							</div>
						</span>
						<Timescale />
						{ContextMenu(contextMenuParams)}
					</div>
				</section>


				<section className="timeline-footer-wrapper">
					<div className="timeline-undoRedo-controller-wrapper">
						<button aria-label="Undo" onClick={history.undo} disabled={!history.canUndo} className="undo pill sz-2">
							<span className="icon" aria-hidden="true" />
							<span className="label">Undo</span>
						</button>
						<button aria-label="Redo" onClick={history.redo} disabled={!history.canRedo} className="redo pill sz-2">
							<span className="icon" aria-hidden="true" />
							<span className="label">Redo</span>
						</button>
					</div>
					<button
						aria-label="Save changes and close"
						onClick={SaveEditMode}
						className="save-edit-mode-button pill sz-2"
						disabled={!history.canUndo && !history.canRedo}
					>
						<span className="icon" aria-hidden="true" />
						<span className="label">Save & close</span>
					</button>
				</section>
			</div>
		</section>
	);
}
